import { IconLibrary } from '@product-framework/index';
import { Box, Button, Pagination } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useSnackbar,
} from '@ui-framework/index';
import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope/ServiceProviderScopeContext';

const IndustriesServiced = forwardRef(({ onChange = () => undefined }, ref) => {
  const nameKey = 'industryName';
  const valueKey = 'id';
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get } = useDataProvider(provider);
  const [industriesServiced, setIndustriesServiced] = useState([]);
  const [selected, setSelected] = useState();
  const limit = 10;
  const { skillsConfig } = useServiceProviderScope();
  const config = skillsConfig ?? {};

  const getPageCount = (total, limitArg) => Math.ceil(total / limitArg);

  const getIndustriesServiced = async (page = 1) => {
    const response = await get(
      `${config?.api?.industriesServiced?.fetch}?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[nameKey],
          value: item[valueKey],
          status: item.status,
        })) || [];
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const initialize = async () => {
    const result = await getIndustriesServiced();
    setIndustriesServiced(result || {});
    const selectedIndustry =
      result?.data?.length > 0 ? result.data[0].value : undefined;
    setSelected(selectedIndustry);
    onChange(
      result?.data?.length > 0 ? result.data[0] : { label: '', value: '' },
    );
  };

  const getButtonColor = (isSelected, status) => {
    if (status === 'Inactive') return 'error';
    if (isSelected) return 'primary';
    return 'secondary';
  };

  useEffect(() => {
    initialize();
  }, []);

  const pageCount = getPageCount(industriesServiced?.total, limit);

  return (
    <Box ref={ref}>
      <Box
        border={1}
        borderColor="divider"
        borderRadius={1}
        sx={{
          padding: 1,
          height: '448px',
          marginBottom: 2,
          marginTop: 2,
          scrollbarWidth: 'thin',
          overflow: 'scroll',
        }}>
        {industriesServiced?.data?.map(industry => (
          <Button
            onClick={() => {
              setSelected(industry.value);
              onChange(industry);
            }}
            {...(selected === industry.value && {
              endIcon: <IconLibrary.Next />,
            })}
            fullWidth
            color={getButtonColor(selected === industry.value, industry.status)}
            variant="contained"
            key={industry.value}
            disabled={industry.status === false}
            sx={{ minHeight: '40px', marginBottom: '5px' }}>
            {industry.label} {industry.status === false ? ' (In-Active)' : ''}
          </Button>
        ))}
      </Box>
      {pageCount > 1 && (
        <Pagination
          onChange={async (event, value) => {
            const newItems = await getIndustriesServiced(value);
            setIndustriesServiced(newItems);
          }}
          sx={{
            mt: 1,
            mr: 1,
            ml: 1,
            justifyContent: 'flex-end',
            display: 'flex',
          }}
          count={pageCount}
          size="small"
        />
      )}
    </Box>
  );
});

export default IndustriesServiced;
